// Team comparison stats panel mirroring DVMate iOS's BeachStatsView: a
// vertical stack of rows, each showing a title, a left(home)/right(away)
// horizontal bar comparison, and the raw value + "(x/y)" sub-value on each
// side. Shows the selected rally's own set when one is picked, otherwise the
// whole match.
import { useMemo } from "react";
import { computeMatchStats } from "../utils/PointByPoint";

const kHomeColour = "#2563eb";
const kAwayColour = "#f97316";

function StatRow({ title, leftValue, rightValue, leftSub, rightSub, isPercent, leftActive, rightActive, onLeftClick, onRightClick }) {
  const maxValue = Math.max(leftValue, rightValue, 0);
  const scaleMax = isPercent ? 100 : maxValue * 1.5 || 1;
  const leftPct = Math.min(100, (leftValue / scaleMax) * 100);
  const rightPct = Math.min(100, (rightValue / scaleMax) * 100);
  const leftIsMax = leftValue >= rightValue && maxValue > 0;
  const rightIsMax = rightValue > leftValue;
  const fmt = (v) => (isPercent ? `${Math.round(v)}%` : Math.round(v));

  return (
    <div className="py-1">
      <p className="text-center text-[11px] uppercase font-semibold opacity-70">{title}</p>
      <div className="flex items-center justify-between text-sm font-bold px-1">
        <button
          type="button"
          onClick={onLeftClick}
          className={"rounded px-1 cursor-pointer" + (leftActive ? " bg-info/30" : " hover:bg-base-300")}
        >
          {fmt(leftValue)}
        </button>
        <button
          type="button"
          onClick={onRightClick}
          className={"rounded px-1 cursor-pointer" + (rightActive ? " bg-info/30" : " hover:bg-base-300")}
        >
          {fmt(rightValue)}
        </button>
      </div>
      <div className="flex items-center h-1.5 rounded-full overflow-hidden bg-base-300">
        <div className="flex-1 flex justify-end h-full">
          <div
            className="h-full"
            style={{ width: `${leftPct}%`, backgroundColor: leftIsMax ? kHomeColour : "#9ca3af" }}
          />
        </div>
        <div className="w-px h-full bg-base-100" />
        <div className="flex-1 flex justify-start h-full">
          <div
            className="h-full"
            style={{ width: `${rightPct}%`, backgroundColor: rightIsMax ? kAwayColour : "#9ca3af" }}
          />
        </div>
      </div>
      <div className="flex items-center justify-between text-[10px] opacity-60 px-1">
        <span>{leftSub}</span>
        <span>{rightSub}</span>
      </div>
    </div>
  );
}

function MomentumStatsPanel({ sets, teamAName, teamBName, t, activeStat, onStatClick }) {
  const stats = useMemo(() => computeMatchStats(sets ?? []), [sets]);

  const rows = [
    { key: "sideout", title: t("Sideout %"), a: stats.A.sideoutPercent, b: stats.B.sideoutPercent, aSub: stats.A.sideoutSub, bSub: stats.B.sideoutSub, percent: true },
    { key: "breakPoint", title: t("Break Point %"), a: stats.A.pointPercent, b: stats.B.pointPercent, aSub: stats.A.pointSub, bSub: stats.B.pointSub, percent: true },
    { key: "firstBallSideout", title: t("First Ball Sideout %"), a: stats.A.firstBallSideoutPercent, b: stats.B.firstBallSideoutPercent, aSub: stats.A.firstBallSub, bSub: stats.B.firstBallSub, percent: true },
    { key: "transition", title: t("Sideout Transition %"), a: stats.A.transitionPercent, b: stats.B.transitionPercent, aSub: stats.A.transitionSub, bSub: stats.B.transitionSub, percent: true },
    { key: "fbKill", title: t("FB Sideout Kills %"), a: stats.A.fbKillPercent, b: stats.B.fbKillPercent, aSub: stats.A.fbKillSub, bSub: stats.B.fbKillSub, percent: true },
    { key: "oppError", title: t("Sideout Opposition Error %"), a: stats.A.oppErrorPercent, b: stats.B.oppErrorPercent, aSub: stats.A.oppErrorSub, bSub: stats.B.oppErrorSub, percent: true },
    { key: "plus", title: t("Plus"), a: stats.A.plus, b: stats.B.plus, aSub: "", bSub: "", percent: false },
    { key: "minus", title: t("Minus"), a: stats.A.minus, b: stats.B.minus, aSub: "", bSub: "", percent: false },
    { key: "blocks", title: t("Blocks"), a: stats.A.blocks, b: stats.B.blocks, aSub: "", bSub: "", percent: false },
    { key: "digs", title: t("Digs"), a: stats.A.digs, b: stats.B.digs, aSub: "", bSub: "", percent: false },
    { key: "positiveServe", title: t("Positive Serve %"), a: stats.A.positiveServePercent, b: stats.B.positiveServePercent, aSub: stats.A.positiveServeSub, bSub: stats.B.positiveServeSub, percent: true },
  ];

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between text-xs font-bold px-1 pb-1 border-b border-base-300">
        <span style={{ color: kHomeColour }}>{teamAName}</span>
        <span style={{ color: kAwayColour }}>{teamBName}</span>
      </div>
      {rows.map((r) => (
        <StatRow
          key={r.key}
          title={r.title}
          leftValue={r.a}
          rightValue={r.b}
          leftSub={r.aSub}
          rightSub={r.bSub}
          isPercent={r.percent}
          leftActive={activeStat?.A === r.key}
          rightActive={activeStat?.B === r.key}
          onLeftClick={() => onStatClick?.("A", r.key, stats.A.rallies[r.key])}
          onRightClick={() => onStatClick?.("B", r.key, stats.B.rallies[r.key])}
        />
      ))}
    </div>
  );
}

export default MomentumStatsPanel;
